// Browser resolver for §27.6's `MobDef.script` names (client adapter, like `data.ts`).
//
// A script name resolves to "`data/scripts/<name>.tfs` **or an overlay-provided
// source**" (§27.6). The sim cannot read a file (ENG-1), so the resolver is the HOST's
// — and in a browser there is no filesystem at all, only what the bundler embedded.
// Vite's `import.meta.glob` does that embedding at build time, exactly as the registry
// loader does for `data/**/*.json`.
//
// Collection sources are globbed rather than one fixed directory because a script name
// arrives from a `MobDef` that may have come from ANY collection's overlay: with the
// opt-in `examples` collection selected, `sheep` names `skittish`, and the source that
// satisfies it lives under that collection. A name nothing provides returns `undefined`,
// which leaves that mob on its archetype rather than breaking it (E-50).
const COLLECTION_SOURCES = import.meta.glob('/data/collections/*/*.tfs', {
  eager: true, query: '?raw', import: 'default',
}) as Record<string, string>;

/** name → source, built once. Collections are scanned in path order, and a duplicate
 *  name keeps the FIRST one seen so the mapping is a pure function of the bundle rather
 *  than of object-key iteration luck — two collections shipping one name is a content
 *  problem for `audit:registry`, not something to resolve arbitrarily here. */
const BY_NAME: ReadonlyMap<string, string> = new Map(
  Object.entries(COLLECTION_SOURCES)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([path, source]) => [path.slice(path.lastIndexOf('/') + 1, -'.tfs'.length), source] as const)
    .filter(([name], i, all) => all.findIndex(([n]) => n === name) === i),
);

/** Every script name the bundle can resolve, sorted — for the editor's picker and for
 *  a diagnostic that wants to say what WAS available when a name did not resolve. */
export function scriptNames(): readonly string[] {
  return [...BY_NAME.keys()].sort();
}

/** §27.6's resolver. `undefined` ⇒ nothing provides that name (E-50: the mob runs its
 *  archetype, and a dangling name never breaks it). */
export function scriptSource(name: string): string | undefined {
  return BY_NAME.get(name);
}
